import { Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, shareReplay, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/services/auth.service';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { OrgUser } from '../../models/user/user';
import { NavigationService } from '../../services/navigation.service';
import { OrganizationConfigs } from '../../models/organization/config';
import { Organization } from '../../models/organization/organization';
import { MessagingService } from '../../services/messaging.service';
import { ActiveModuleMap, NAV_ROUTES, NavRoute, isNavRouteReachable } from './navigation-routes';
import { crossAppDestination, ProductApp } from './module-transfer';

// ✅ Akita imports - using facades only
import { UserFacadeService } from 'src/app/auth/state/user-facade.service';
import { OrganizationFacadeService } from '../../state/organization/organization-facade.service';
import { OrganizationConfigFacadeService } from '../../state/organization-config/organization-config-facade.service';
import { UserOrganizationsFacadeService } from '../../state/user-organizations/user-organizations-facade.service';
import { BrandService } from '../../services/brand.service';
import { OrganizationSwitchService } from '../../services/organization-switch.service';
import { ShellChromeService } from '../../services/shell-chrome.service';
import { MatSidenavContainer, MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { MatToolbar } from '@angular/material/toolbar';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatNavList, MatListItem, MatDivider } from '@angular/material/list';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ClickStopPropagationDirective } from '../../directives/click-stop-propagation.directive';
import { AsyncPipe } from '@angular/common';
import { LanguageSwitcherComponent } from '../language-switcher/language-switcher.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

interface VisibleRoute extends NavRoute {
  visible: boolean;
  name: string;
  destination: ProductApp | null;
  link: string[];
  accessibleName: string;
}

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatSidenavContainer,
    MatSidenav,
    MatToolbar,
    MatFormField,
    MatSelect,
    MatOption,
    MatNavList,
    MatListItem,
    RouterLink,
    MatDivider,
    MatSidenavContent,
    MatIconButton,
    MatIcon,
    MatButton,
    ClickStopPropagationDirective,
    RouterOutlet,
    AsyncPipe,
    LanguageSwitcherComponent,
    TranslocoDirective,
  ],
})
export class NavigationComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // ✅ Inject facades only (facades access queries internally)
  private breakpointObserver = inject(BreakpointObserver);
  private authService = inject(AuthService);
  private organizationSwitch = inject(OrganizationSwitchService);
  private messagingService = inject(MessagingService);
  private navigationService = inject(NavigationService);
  private userFacade = inject(UserFacadeService);
  private orgFacade = inject(OrganizationFacadeService);
  private orgConfigFacade = inject(OrganizationConfigFacadeService);
  private userOrgsFacade = inject(UserOrganizationsFacadeService);
  private brandService = inject(BrandService);
  private router = inject(Router);
  private transloco = inject(TranslocoService);

  /** The active route owns the page chrome and the document scroll (mobile detail routes). */
  readonly bareShell = inject(ShellChromeService).bare;
  readonly documentScroll = inject(ShellChromeService).documentScroll;

  // Bridge the brand modules signal to an observable for the RxJS pipelines below
  private brandModules$ = toObservable(this.brandService.availableModules);

  // ✅ Observables from facades
  title$: Observable<string> = this.navigationService.getTitle();
  organization$: Observable<Organization> = this.orgFacade.organization$.pipe(
    distinctUntilChanged((prev, curr) => prev.id === curr.id) // Only emit when org actually changes
  );

  userOrganizations$: Observable<Organization[]> = this.userOrgsFacade.organizations$.pipe(
    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
  );

  selectedOrg$: Observable<Organization> = this.orgFacade.organization$.pipe(
    distinctUntilChanged((prev, curr) => prev.id === curr.id)
  );

  // ✅ Computed observable for visible routes (prevents constant recalculation in template)
  visibleRoutes$: Observable<VisibleRoute[]>;

  // Brand-aware profile visibility
  isProfileAvailable$: Observable<boolean>;

  // Component state
  showBackNavigation: boolean = true;

  routes: NavRoute[] = NAV_ROUTES;

  isHandset$: Observable<boolean>;

  constructor() {
    this.isHandset$ = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
      map((result) => result.matches),
      distinctUntilChanged(), // Only emit when it actually changes
      shareReplay(1)
    );

    // ✅ Handle back navigation visibility
    // this.router.events.pipe(
    //   takeUntil(this.destroy$),
    //   filter(event => event instanceof NavigationEnd)
    // ).subscribe(event => {
    //   const navEvent = event as NavigationEnd;
    //   this.showBackNavigation = !navEvent.url.includes('customer-details/new-order');
    // });

    // ✅ Compute visible routes ONCE when orgUser, activeModules, brand modules, or active lang change
    this.visibleRoutes$ = combineLatest([
      this.userFacade.orgUser$.pipe(
        distinctUntilChanged(
          (prev, curr) => prev.id === curr.id && JSON.stringify(prev.roles) === JSON.stringify(curr.roles)
        )
      ),
      this.orgConfigFacade.activeModules$.pipe(
        debounceTime(100), // Wait for all config updates to settle
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      ),
      this.brandModules$.pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))),
      this.transloco.langChanges$,
    ]).pipe(
      map(([orgUser, activeModules, brandModules]) => {
        const isTx = this.brandService.isTxMode();
        return this.routes.map((route) => {
          const translated = this.transloco.translate<string>(`nav.routes.${route.labelKey ?? route.moduleName}`);
          const destination = crossAppDestination(
            route,
            new URL(window.location.href),
            this.brandService.isWhiteLabelMode()
          );
          const name = isTx ? translated.replace(/^TX (.)/u, (_, first: string) => first.toUpperCase()) : translated;
          return {
            ...route,
            // Strip the "TX " prefix on the TX domain and capitalize what remains ("Upravljanje", not "TX upravljanje")
            name,
            destination,
            link: destination ? ['/app/module-transfer', route.path] : ['/app', route.path],
            accessibleName: destination
              ? this.transloco.translate('nav.crossAppLink', {
                  name,
                  app: destination === 'business' ? 'Business' : 'TX',
                })
              : name,
            visible: this.checkVisibility(route, orgUser, activeModules, brandModules, destination),
          };
        });
      }),
      shareReplay(1) // Cache the result
    );

    // Profile is always shown in Tefter mode (brandModules includes '*'),
    // and only shown in private-label if 'profile' is in the allowed modules list
    this.isProfileAvailable$ = this.brandModules$.pipe(
      map((modules) => modules.includes('*') || modules.includes('profile')),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  ngOnInit(): void {
    this.messagingService.requestPermission();
    this.messagingService.receiveMessage();

    // ✅ Initialize user organizations (only once)
    this.userOrgsFacade.loadUserOrganizations();
  }

  /**
   * Check if route is visible (pure function - no side effects)
   */
  private checkVisibility(
    route: NavRoute,
    orgUser: OrgUser,
    activeModules: OrganizationConfigs.ActiveModules,
    brandModules: string[],
    destination: ProductApp | null = null
  ): boolean {
    return isNavRouteReachable(route, orgUser?.roles ?? [], activeModules as ActiveModuleMap, brandModules, {
      skipBrandCheck: !!destination,
      brandEntitles: !destination && this.brandService.isWhiteLabelMode(),
    });
  }

  signOut() {
    this.authService.clearAllStatesAndSignout();
  }

  navigateBack() {
    this.navigationService.goBack();
  }

  compareOrgs(org1: any, org2: any): boolean {
    return org1?.id === org2?.id;
  }

  /**
   * Switch organization and reload
   */
  async changeOrg(selectedOrg: Organization) {
    if (!selectedOrg?.id) {
      return;
    }

    // Reload to the same URL after the switch, except from a page that cannot
    // be shown for the new organization.
    const currentUrl = this.router.url;
    const reloadUrl =
      currentUrl && currentUrl.startsWith('/app/') && !currentUrl.startsWith('/app/access-denied') ? currentUrl : '/';
    await this.organizationSwitch.switchTo(selectedOrg.id, reloadUrl);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
